import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createClientPrice,
  getClientPrice,
  updateClientPrice,
  resetCreateMessages,
  resetUpdateMessages,
} from "../../../store/slices/clientPriceSlice";
import { getAllClinic } from "../../../store/slices/clinicSlice";
import { getPathologyServiceByClinic } from "../../../store/slices/serviceSlice";
import Table from "../../common/Table/table";
import {
  showToastifySuccess,
  showToastifyError,
} from "../../common/tostify/Toastifyresponse";
import ShowTostifyMessageAndReset from "../../common/showTostifyMessageAndReset/showTostifyMessageAndReset";
import AddUpdateClientPrice from "./AddUpdateClientPrice";

const ClientPrice = () => {
  const dispatch = useDispatch();

  const [showModal, setModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [formData, setFormData] = useState({
    clientId: "",
    clientName: "",
    serviceId: "",
    serviceName: "",
    clinicId: "",
    price: "",
    serviceType: "",
    status: "Active",
  });
  const [searchValue, setSearchValue] = useState("");
  const [pageLimit, setPageLimit] = useState(10);

  const showTostifyMessageAndReset = ShowTostifyMessageAndReset();
  const { clinicInfo, allClinicLists } = useSelector((state) => state.clinic);

  const { pathologyServices } = useSelector((state) => state.service);
  const {
    clientPrice,
    createclientPriceSuccessMessage,
    createclientPriceErrorMessage,
    updateClientPriceErrorMessage,
    updateClientPriceSuccessMessage,
    currentPage,
    totalPages,
    totalCount,
    loading,
  } = useSelector((state) => state.clientPrice);

  const clinicId = clinicInfo?.[0]?.clinicId;
  const { token } = useSelector((state) => state.auth);

  // fetch wrapper with pagination & search support
  const fetchClientPrice = useCallback(
    (page = 1, lim = pageLimit, search = searchValue) => {
      if (clinicId && token) {
        dispatch(
          getClientPrice({
            clinicId,
            token,
            currentPage: page,
            limit: lim,
            searchQuery: search || undefined,
            loginRole: "Client",
          })
        );
      }
    },
    [clinicId, token, dispatch, pageLimit, searchValue]
  );

  useEffect(() => {
    if (clinicId) {
      dispatch(
        getAllClinic({
          clinicId,
          loginRole: "Client",
          token,
        })
      );
      fetchClientPrice();
    }
  }, [dispatch,token, fetchClientPrice, clinicId]);

  useEffect(() => {
    dispatch(
      getPathologyServiceByClinic({
        clinicId,
        token,
      })
    );
  }, [dispatch, token, clinicId]);

  useEffect(() => {
    showTostifyMessageAndReset(
      createclientPriceSuccessMessage,
      showToastifySuccess,
      resetCreateMessages
    );
    showTostifyMessageAndReset(
      createclientPriceErrorMessage,
      showToastifyError,
      resetCreateMessages
    );
    showTostifyMessageAndReset(
      updateClientPriceSuccessMessage,
      showToastifySuccess,
      resetUpdateMessages
    );
    showTostifyMessageAndReset(
      updateClientPriceErrorMessage,
      showToastifyError,
      resetUpdateMessages
    );
  }, [
    createclientPriceSuccessMessage,
    createclientPriceErrorMessage,
    updateClientPriceSuccessMessage,
    updateClientPriceErrorMessage,
    dispatch,
    showTostifyMessageAndReset,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, name) => {
    if (name === "client") {
      setFormData((prev) => ({
        ...prev,
        clientId: selectedOption.value,
        clientName: selectedOption.label,
      }));
    } else if (name === "service") {
      setFormData((prev) => ({
        ...prev,
        serviceId: selectedOption.value,
        serviceName: selectedOption.label,
        serviceType: selectedOption.type,
      }));
    }
  };

  const handleModal = () => {
    setFormData({
      clientId: "",
      clientName: "",
      serviceId: "",
      serviceName: "",
      price: "",
      status: "Active",
    });
    setModalVisible(true);
    setModalAction("create");
  };

  const handleEdit = (item) => {
    setFormData({
      clientId: item.clientId,
      clientName: item.client,
      serviceId: item.itemId,
      serviceName: item.service,
      serviceType: item.itemType,
      price: item.price,
      status: item.status,
    });
    setModalVisible(true);
    setModalAction("edit");
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    fetchClientPrice(1, pageLimit, val);
  };

  const handleAddOrUpdateClientPrice = async (e) => {
    e.preventDefault();
    try {
      const clientDetails = [
        {
          itemId: formData.serviceId,
          itemType: formData.serviceType || "test",
          price: formData.price,
        },
      ];

      if (modalAction === "create") {
        const payload = {
          clientId: formData.clientId,
          clinicId,
          clientDetails,
          token,
        };

        await dispatch(createClientPrice(payload));
      } else if (modalAction === "edit") {
        for (const detail of clientDetails) {
          const payload = {
            clientId: formData.clientId,
            clinicId,
            itemId: detail.itemId,
            itemType: detail.itemType,
            price: detail.price,
            token,
          };

          await dispatch(updateClientPrice(payload));
        }

      }
      fetchClientPrice();
      setModalVisible(false);
      setFormData({
        clientId: "",
        clientName: "",
        serviceId: "",
        serviceName: "",
        price: "",
        status: "Active",
      });
    } catch (error) {
      console.error("Error occurred:", error);
    }
  };

  const handlePageChange = (page) => {
    fetchClientPrice(page, pageLimit, searchValue);
  };

  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    fetchClientPrice(1, newLimit, searchValue);
  };

  const tableHead = [
    { title: "Client Name", key: "client" },
    { title: "Test Name", key: "service" },
    { title: "Client Price", key: "price" },
  ];

  return (
    <div className="container mt-4">
      <Table
        tableHead={tableHead}
        addModal={handleModal}
        handleAddBtn={true}
        tableBody={
          clientPrice &&
          clientPrice.flatMap((item) => {
            return item.clientDetails.map((detail) => ({
              id: item?._id,
              client: item?.clientId?.userId?.name,
              clientId: item?.clientId?._id,
              service: detail?.name,
              price: detail?.price,
              itemId: detail?.itemId,
              itemType: detail?.itemType,
            }));
          })
        }
        handleSearchOption={true}
        handleSearch={handleSearch}
        handleEdit={handleEdit}
        hideStatusTab={true}
        handleShare={false}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        limit={pageLimit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        showPagination={true}
        showLimitDropdown={true}
        loading={loading}
        isLoading={loading}
      />

      <AddUpdateClientPrice
        showModal={showModal}
        setModalVisible={setModalVisible}
        modalAction={modalAction}
        formData={formData}
        handleChange={handleChange}
        handleSelectChange={handleSelectChange}
        clientOptions={allClinicLists}
        serviceOptions={pathologyServices}
        allClinicLists={allClinicLists}
        pathologyServices={pathologyServices}
        handleAddOrUpdateClientPrice={handleAddOrUpdateClientPrice}
      />
    </div>
  );
};

export default ClientPrice;